import { Component, HostListener, NgZone } from '@angular/core'
import { Subject, Subscription, timer } from 'rxjs'
import { Router, NavigationStart, NavigationEnd, ActivatedRoute } from '@angular/router'
import { AuthService } from '@app/core/_services/auth.service'
import { UserService } from '@app/core/_services/user.service'
import { takeUntil, take, distinctUntilChanged } from 'rxjs/operators'
import { Meta, Title } from '@angular/platform-browser'
import { environment } from '@env/environment'
import { StoreRedirectOnLoginService } from './core/_services/store-redirect-on-login.service'
import { AnalyticsService } from './core/_services/analytics.service'
import { ConfirmedSessionsResponse, Container, User } from './core/_models'
import { DialogService } from './core/_services/dialog.service'
import {
    BookSessionsService,
    ConfirmedSessionsService,
    MeetingsService,
    UtilsService,
} from './core/_services'
import { BrowserNotificationsService } from './core/_services/browser-notifications.service'
import { DeviceDetectionService } from './core/_services/device-detection.service'
import { AutoCancelStatusService } from './core/_services/auto-cancel-status.service'
import { type User as FirebaseUser } from 'firebase/auth'
import { SoftwareUpdateService } from './core/_services/software-update.service'
import { ServerTimeService } from './core/_services/server-time.service'
import { RewardsReferralService } from './core/_services/rewards-referral.service'
import { ViewportService } from './core/_services/viewport.service'
import { WelcomeChecklistService } from './core/_services/welcome-checklist.service'
import { SystemBannerService } from './core/_services/system-banner.service'
import {
    UserRelationshipActionTaken,
    UserRelationshipService,
} from './core/_services/user-relationship.service'
import { FeatureCheckService } from './core/_services/feature-check.service'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { TaskService } from './core/_services/task.service'
import { PosthogMarketingFunnelService } from './core/_services/posthog-marketing-funnel.service'
import { RealtimeService } from '@app/core/_services/realtime.service'
import { RealtimeDisconnectReason } from '@app/core/_services/realtime.types'
import { GroupMeetingCancellationNoticeService } from '@app/core/_services/group-meeting-cancellation-notice.service'

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent {
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    private ngUserUnsubscribe: Subject<any> = new Subject<any>()
    private ngWebWorkerUnsubscribe: Subject<any> = new Subject<any>()
    currentUrl: string = ''

    public showMaintenanceBanner: boolean = false
    public isBannerSticky: boolean = false
    public termsCreated: boolean = false
    private webWorker: Worker
    public systemBanner: string = null
    private isAuthenticated: boolean

    public threeColumnLayout = false
    public hideBookingHubOnMobile = false
    public readonly isGroupMeetingsBetaEnabled =
        this.featureCheckService.groupMeetingsBetaEnabledSig

    // Prevent confirmed session / task list refresh from going out when window is blurred
    private timedOut: boolean = false
    private blurred: boolean = false
    private confirmedSessionPollingSub?: Subscription
    private realtimeUserId: string | null = null

    public leftPanelAnimationClass: string = 'animation-slide-right-and-fade-in-far-snappy'
    public middleContentAnimationClass: string = 'animation-slide-up-and-fade-in-far-snappy'
    public rightPanelAnimationClass: string = 'animation-slide-left-and-fade-in-far-snappy'

    public faInfoCircle = faInfoCircle

    constructor(
        private analyticsService: AnalyticsService,
        private serverTimeService: ServerTimeService,
        public userService: UserService,
        public router: Router,
        private authService: AuthService,
        private metaService: Meta,
        private activatedRoute: ActivatedRoute,
        private storeRedirectOnLoginService: StoreRedirectOnLoginService,
        private dialogService: DialogService,
        private utilsService: UtilsService,
        private titleService: Title,
        private confirmedSessionsService: ConfirmedSessionsService,
        private meetingsService: MeetingsService,
        private browserNotificationsService: BrowserNotificationsService,
        private deviceDetectionService: DeviceDetectionService,
        private ngZone: NgZone,
        private autoCancelStatusService: AutoCancelStatusService,
        private softwareUpdateService: SoftwareUpdateService,
        private rewardsReferralService: RewardsReferralService,
        public viewportService: ViewportService,
        public welcomeChecklistService: WelcomeChecklistService,
        private systemBannerService: SystemBannerService,
        private featureCheckService: FeatureCheckService,
        private bookSessionsService: BookSessionsService,
        private userRelationshipService: UserRelationshipService,
        private taskService: TaskService,
        private posthogMarketingFunnelService: PosthogMarketingFunnelService,
        private realtimeService: RealtimeService,
        private groupMeetingCancellationNoticeService: GroupMeetingCancellationNoticeService,
    ) {}

    @HostListener('window:focus', ['$event'])
    onFocus(event: any): void {
        this.blurred = false

        if (this.timedOut === true) {
            this.timedOut = false
            this.queryConfirmedSessions()
        }
    }

    @HostListener('window:blur', ['$event'])
    onBlur(event: any): void {
        this.blurred = true
    }

    @HostListener('window:mousemove', ['$event'])
    onMouseMove(event: any): void {
        if (this.blurred === true && this.timedOut === true) {
            this.timedOut = false
            this.queryConfirmedSessions(true)
        }
    }

    getChild(activatedRoute: ActivatedRoute) {
        if (activatedRoute.firstChild) {
            return this.getChild(activatedRoute.firstChild)
        } else {
            return activatedRoute
        }
    }

    updateMaintenanceBannerState() {
        this.showMaintenanceBanner = this.utilsService.inMaintenanceWindow(this.currentUrl)
        this.userService.currentUser.pipe(take(1)).subscribe((user: User) => {
            if (user.planInfo.addressUpdateNeeded || this.showMaintenanceBanner) {
                this.isBannerSticky = this.utilsService.isMaintenanceBannerSticky()
            }
        })
    }

    private showUpdatedTermsModalIfNeeded() {
        this.userService.currentUser.pipe(take(1)).subscribe((user: User) => {
            // If user is joining a session, do not present the terms in order
            // avoid a potentially awkward experience with their partner
            if (user.onboard && !this.currentUrl.startsWith('/session/')) {
                // This check ensures that that if the user has not accepted the
                // latest version of the ToS/Privacy Policy AND
                // this version of the application has the acknowledgment modal for
                // the latest version, then present it. This ensures that those on older
                // versions of the application do not show the older version of the acknowledgment
                // modal
                // NOTE: Comment the below out for now as we do not have a ToS modal
                // for the latest ToS changes. The Labs announcement modal is currently
                // doing that for us in the calendar component.
                // Once we decide to remove that announcement, we will need to implement
                // a ToS standalone modal and uncomment the below code
                // if (
                //     user.terms.accepted === false &&
                //     user.terms.latestVersion === environment.tosVersion
                // ) {
                //     this.dialogService.openUpdatedToSAgreementModal()
                // }
            }
        })
    }

    private showSuspendedDialogIfNeeded() {
        this.userService.currentUser.pipe(take(1)).subscribe((user: User) => {
            if (user.suspended) {
                this.dialogService.openSuspendedDialog()
            }
        })
    }

    private queryConfirmedSessions(override: boolean = false) {
        if (this.blurred === false || override) {
            this.meetingsService.getMeetingsBookings().subscribe()
            this.taskService.fetchIncompleteTasks().subscribe()
        }
    }

    private trackFacebookPageView(): void {
        const facebookPixel = (window as any).fbq
        if (typeof facebookPixel !== 'function') {
            return
        }

        try {
            facebookPixel('track', 'PageView')
        } catch (error) {
            console.error('Facebook Pixel page view tracking failed', error)
        }
    }

    private updateIntercomVisibility(): void {
        const intercom = (window as any).Intercom
        if (typeof intercom !== 'function') {
            return
        }

        try {
            intercom('update', { hide_default_launcher: true })
        } catch (error) {
            console.error('Intercom update failed', error)
        }
    }

    ngOnInit() {
        this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.titleService.setTitle(this.utilsService.getPageTitleFromRoute(event.url))
                const routeData = this.getChild(this.activatedRoute).snapshot.data
                if (environment.sentryEnv !== 'production') {
                    this.metaService.updateTag({ name: 'robots', content: 'nofollow,noindex' })
                } else {
                    if (routeData.robots) {
                        this.metaService.updateTag({ name: 'robots', content: routeData.robots })
                    } else {
                        this.metaService.updateTag({ name: 'robots', content: 'follow,index' })
                    }
                }

                this.threeColumnLayout = routeData?.threeColumnLayout ?? false
                this.hideBookingHubOnMobile = routeData?.hideBookingHubOnMobile ?? false
                this.analyticsService.trackPageView(event.urlAfterRedirects, routeData)
                this.posthogMarketingFunnelService.trackPreAuthPageViewIfEligible(
                    event.urlAfterRedirects,
                )
                this.trackFacebookPageView()
                this.updateIntercomVisibility()

                this.currentUrl = event.urlAfterRedirects
                setTimeout(() => {
                    this.updateMaintenanceBannerState()
                })

                this.softwareUpdateService.checkForUpdate()
                this.showUpdatedTermsModalIfNeeded()
                this.showSuspendedDialogIfNeeded()
            }

            if (event instanceof NavigationStart) {
                this.userService.setSignUpLoginViaSocialInviteeFlow(event.url)
                this.storeRedirectOnLoginService.resetUrlToRedirectToOnLogin(event.url)
                this.currentUrl = event.url
            }
        })

        this.authService.getFirebaseUser().then((currentUser) => {
            // TODO: Refactor how we move to dashboard
            // on initial page load if user already logged in
            if (currentUser && this.currentUrl === '/') {
                this.router.navigate(['/dashboard'])
            }
        })

        this.authService.authState$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((user: FirebaseUser | null) => {
                if (!user && this.isAuthenticated) {
                    this.analyticsService.logUserSignedOut('nullFirebaseUser')
                }

                //handle auth state changes here. Note, that user will be null if there is no currently logged in user.
                this.userService.populate(user)
            })

        this.autoCancelStatusService.autocancelStatus
            .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
            .subscribe((enabled: boolean) => {
                if (enabled) {
                    this.dialogService.openNoShowAcknowledgeDialog(
                        this.autoCancelStatusService.triggerSession,
                    )
                }
            })

        // on log in / log out, set up or destroy the web worker as needed
        this.userService.isAuthenticated
            .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
            .subscribe((isAuthenticated) => {
                this.isAuthenticated = isAuthenticated
                if (isAuthenticated) {
                    // clear referral code on any sign in/sign up
                    this.rewardsReferralService.clearRewardsReferralCode()

                    this.userService.currentUser.pipe(take(1)).subscribe((user) => {
                        this.serverTimeService.checkDeviceTime()
                        // set up or destroy the web worker as needed
                        this.setupSessionReminders()
                        this.groupMeetingCancellationNoticeService.start()
                        this.startConfirmedSessionQueries()
                        this.connectRealtimeIfEnabled(user)
                    })
                } else {
                    this.groupMeetingCancellationNoticeService.stop()
                    this.disconnectRealtime('signed_out')
                    this.destroyWebWorker()
                    this.stopConfirmedSessionQueries()
                }
            })

        this.systemBannerService.bannerSubject.subscribe((banner) => {
            this.systemBanner = banner
        })

        // animations!
        this.bookSessionsService.joiningSession$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((joining) => {
                if (joining) {
                    this.leftPanelAnimationClass = 'animation-slide-left-and-fade-out-far-snappy'
                    this.middleContentAnimationClass =
                        'animation-slide-down-and-fade-out-far-snappy'
                    this.rightPanelAnimationClass = 'animation-slide-right-and-fade-out-far-snappy'
                } else {
                    this.leftPanelAnimationClass = 'animation-slide-right-and-fade-in-far-snappy'
                    this.middleContentAnimationClass = 'animation-slide-up-and-fade-in-far-snappy'
                    this.rightPanelAnimationClass = 'animation-slide-left-and-fade-in-far-snappy'
                }
            })
    }

    private startConfirmedSessionQueries(): void {
        this.ngZone.runOutsideAngular(() => {
            this.confirmedSessionPollingSub = timer(0, 120000)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(() => {
                    this.ngZone.run(() => {
                        this.queryConfirmedSessions()
                        if (this.blurred) {
                            this.timedOut = true
                        }
                    })
                })
        })

        // Whenever a user is blocked/unblocked/snoozed, we need to requery
        // confirmed session to update the calendar and any dynamic content
        // that makes use of the ConfirmedSessions array
        this.userRelationshipService.userRelationUpdated
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((actionData: UserRelationshipActionTaken) => {
                if (
                    actionData.action === 'block' ||
                    actionData.action === 'unblock' ||
                    actionData.action === 'bulk-unblock' ||
                    actionData.action === 'snooze' ||
                    actionData.action === 'unsnooze' ||
                    actionData.action === 'blocks-to-snoozes'
                ) {
                    // Refresh confirmed data after a short delay. Block/Unblock/Snooze
                    // will trigger worker process to match/break up matches affected by
                    // these operations. Adding a delay isn't a gaurantee that background tasks
                    // will have completed, but should reduce the chance of out of sync data
                    // NOTE: Investigate approach to make this more reliable once the old layout
                    // has been removed. Doing so while both are active would be more complex
                    setTimeout(() => {
                        this.queryConfirmedSessions(true)
                    }, 3000)
                }
            })
    }

    private stopConfirmedSessionQueries(): void {
        this.confirmedSessionPollingSub?.unsubscribe()
        this.confirmedSessionPollingSub = undefined
    }

    async setupSessionReminders() {
        // TODO: push notifications aaaaaaah
        if (await this.deviceDetectionService.isMobileAccurate()) {
            return
        }

        if (typeof Worker === 'undefined') {
            return // web workers are not supported in this environment.
        }

        // request permissions
        await this.browserNotificationsService.requestNotificationPermissions()

        // listen for any changes that will affect whether or not the web worker should be running
        this.browserNotificationsService.notificationsPermissionState
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.toggleWebWorkerIfNeeded())
        this.userService.currentUser
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.toggleWebWorkerIfNeeded())
    }

    // if all permissions and settings are met, turn the web worker on. if not, turn it off
    toggleWebWorkerIfNeeded() {
        const permissionsState =
            this.browserNotificationsService.getCurrentNotificationsPermissionsState()
        this.userService.currentUser.pipe(take(1)).subscribe((user) => {
            if (permissionsState === 'granted' && user.settings.sessionRemindersEnabled) {
                this.initializeWebWorker(user)
            } else {
                this.destroyWebWorker()
            }
        })
    }

    initializeWebWorker(user: User) {
        // avoid creating a duplicate
        this.destroyWebWorker()

        this.webWorker = new Worker(new URL('./app.worker', import.meta.url))

        this.meetingsService.meetingsBookingsSubject
            .pipe(takeUntil(this.ngWebWorkerUnsubscribe))
            .subscribe((eventsBookingsState) => {
                this.webWorker.postMessage({
                    type: 'sessionsDataUpdated',
                    data: {
                        meetings: eventsBookingsState.meetings,
                    },
                })
            })

        // message the worker when the notification offset changes
        this.userService.currentUser
            .pipe(takeUntil(this.ngWebWorkerUnsubscribe))
            .subscribe((user: User) => {
                this.webWorker.postMessage({
                    type: 'userSettingsChanged',
                    data: {
                        notificationOffset: user.settings.sessionReminderNotificationOffset,
                        timeFormat: user.settings.timeFormat,
                    },
                })
            })

        // respond to the web worker's messages
        this.webWorker.onmessage = ({ data }) => {
            if (data.action) {
                if (data.action === 'updateNextSessionTimestamp') {
                    this.confirmedSessionsService
                        .queryForNextSessionTime()
                        .subscribe((response) => {
                            this.webWorker.postMessage({
                                type: 'updateNextSessionTimestampResponse',
                                data: response,
                            })
                        })
                } else if (data.action === 'notify') {
                    this.browserNotificationsService.notify(data.title, data.options)
                } else if (data.action === 'setLocalStorage') {
                    localStorage.setItem(data.key, data.value)
                } else if (data.action === 'getLocalStorage') {
                    this.webWorker.postMessage({
                        type: 'getLocalStorageResponse',
                        data: localStorage.getItem(data.key),
                    })
                }
            }
        }
    }

    destroyWebWorker() {
        if (this.webWorker) {
            this.webWorker.terminate()
            this.webWorker = null
        }
        this.ngWebWorkerUnsubscribe.next(null)
    }

    ngOnDestroy() {
        this.groupMeetingCancellationNoticeService.stop()
        this.disconnectRealtime('app_destroyed')
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
        this.ngUserUnsubscribe.next(null)
        this.ngUserUnsubscribe.complete()
        this.ngWebWorkerUnsubscribe.next(null)
        this.ngWebWorkerUnsubscribe.complete()
    }

    private connectRealtimeIfEnabled(user: User): void {
        const userId = user?.userId || null
        if (!userId || !this.featureCheckService.isRealtimeEnabled(user)) {
            this.disconnectRealtime(userId ? 'realtime_disabled' : 'missing_user')
            return
        }

        if (this.realtimeUserId && this.realtimeUserId !== userId) {
            this.realtimeService.disconnect('user_changed')
        }

        this.realtimeUserId = userId
        this.realtimeService.connect()
    }

    private disconnectRealtime(reason: RealtimeDisconnectReason = 'client_disconnect'): void {
        this.realtimeUserId = null
        this.realtimeService.disconnect(reason)
    }
}
